const MIME = Object.freeze({
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  csv: "text/csv",
  text: "text/plain",
  rtf: "application/rtf"
});

const NS = Object.freeze({
  relationship: "http://schemas.openxmlformats.org/officeDocument/2006/relationships"
});

function textDecoder(bytes) {
  return new TextDecoder("utf-8").decode(bytes);
}

function parseXml(text) {
  const doc = new DOMParser().parseFromString(text, "application/xml");
  const error = doc.querySelector("parsererror");
  if (error) throw new Error("OFFICE_XML_PARSE_FAILED");
  return doc;
}

function nodesByLocal(root, name) {
  return [...root.getElementsByTagName("*")].filter(node => node.localName === name);
}

function firstByLocal(root, name) {
  return nodesByLocal(root, name)[0] || null;
}

function attrByLocal(node, name) {
  if (!node?.attributes) return "";
  for (const attr of [...node.attributes]) if (attr.localName === name) return attr.value || "";
  return "";
}

function normalizePath(path) {
  const stack = [];
  for (const part of String(path || "").replaceAll("\\", "/").split("/")) {
    if (!part || part === ".") continue;
    if (part === "..") stack.pop();
    else stack.push(part);
  }
  return stack.join("/");
}

function resolveTarget(baseFile, target) {
  if (/^[a-z]+:/i.test(target)) return target;
  const base = String(baseFile || "").split("/").slice(0, -1).join("/");
  return normalizePath(`${base}/${target}`);
}

function makeElement(tag, className = "", text = "") {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (text !== "") el.textContent = text;
  return el;
}

function mimeFromFilename(name = "") {
  const ext = String(name).toLowerCase().split(".").pop();
  return ({
    png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif", webp: "image/webp",
    svg: "image/svg+xml", bmp: "image/bmp", tif: "image/tiff", tiff: "image/tiff", emf: "image/emf", wmf: "image/wmf"
  })[ext] || "application/octet-stream";
}

class ZipArchive {
  constructor(arrayBuffer) {
    this.buffer = arrayBuffer;
    this.bytes = new Uint8Array(arrayBuffer);
    this.view = new DataView(arrayBuffer);
    this.entries = new Map();
    this.parse();
  }

  parse() {
    const min = Math.max(0, this.bytes.length - 65557);
    let eocd = -1;
    for (let i = this.bytes.length - 22; i >= min; i -= 1) {
      if (this.view.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
    }
    if (eocd < 0) throw new Error("OFFICE_ZIP_EOCD_NOT_FOUND");
    const total = this.view.getUint16(eocd + 10, true);
    let offset = this.view.getUint32(eocd + 16, true);
    for (let i = 0; i < total; i += 1) {
      if (this.view.getUint32(offset, true) !== 0x02014b50) throw new Error("OFFICE_ZIP_CENTRAL_DIRECTORY_INVALID");
      const flags = this.view.getUint16(offset + 8, true);
      const method = this.view.getUint16(offset + 10, true);
      const compressedSize = this.view.getUint32(offset + 20, true);
      const uncompressedSize = this.view.getUint32(offset + 24, true);
      const nameLen = this.view.getUint16(offset + 28, true);
      const extraLen = this.view.getUint16(offset + 30, true);
      const commentLen = this.view.getUint16(offset + 32, true);
      const localOffset = this.view.getUint32(offset + 42, true);
      const nameBytes = this.bytes.slice(offset + 46, offset + 46 + nameLen);
      const name = new TextDecoder((flags & 0x800) ? "utf-8" : "utf-8").decode(nameBytes);
      this.entries.set(name, { name, method, compressedSize, uncompressedSize, localOffset });
      offset += 46 + nameLen + extraLen + commentLen;
    }
  }

  names() { return [...this.entries.keys()]; }
  has(name) { return this.entries.has(name); }

  async read(name) {
    const entry = this.entries.get(name);
    if (!entry) return null;
    const offset = entry.localOffset;
    if (this.view.getUint32(offset, true) !== 0x04034b50) throw new Error("OFFICE_ZIP_LOCAL_HEADER_INVALID");
    const nameLen = this.view.getUint16(offset + 26, true);
    const extraLen = this.view.getUint16(offset + 28, true);
    const start = offset + 30 + nameLen + extraLen;
    const compressed = this.bytes.slice(start, start + entry.compressedSize);
    if (entry.method === 0) return compressed;
    if (entry.method !== 8) throw new Error(`OFFICE_ZIP_COMPRESSION_UNSUPPORTED_${entry.method}`);
    if (typeof DecompressionStream === "undefined") throw new Error("BROWSER_DEFLATE_UNAVAILABLE");
    const stream = new Blob([compressed]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  }

  async text(name) {
    const bytes = await this.read(name);
    return bytes ? textDecoder(bytes) : "";
  }
}

async function relationshipMap(zip, relPath, baseFile) {
  if (!zip.has(relPath)) return new Map();
  const doc = parseXml(await zip.text(relPath));
  const map = new Map();
  for (const rel of nodesByLocal(doc, "Relationship")) {
    const id = rel.getAttribute("Id") || "";
    const target = rel.getAttribute("Target") || "";
    const mode = rel.getAttribute("TargetMode") || "";
    if (id) map.set(id, { target: mode === "External" ? target : resolveTarget(baseFile, target), external: mode === "External" });
  }
  return map;
}

function paragraphStyle(paragraph) {
  const pPr = [...paragraph.children].find(node => node.localName === "pPr");
  const style = pPr ? firstByLocal(pPr, "pStyle") : null;
  return attrByLocal(style, "val").toLowerCase();
}

function appendRun(container, run) {
  const textNodes = nodesByLocal(run, "t");
  const text = textNodes.map(node => node.textContent || "").join("");
  const breaks = nodesByLocal(run, "br").length;
  const tabs = nodesByLocal(run, "tab").length;
  if (!text && !breaks && !tabs) return;
  const span = document.createElement("span");
  const rPr = [...run.children].find(node => node.localName === "rPr");
  if (rPr) {
    if (firstByLocal(rPr, "b")) span.style.fontWeight = "700";
    if (firstByLocal(rPr, "i")) span.style.fontStyle = "italic";
    if (firstByLocal(rPr, "u")) span.style.textDecoration = "underline";
    const color = attrByLocal(firstByLocal(rPr, "color"), "val");
    if (/^[0-9A-F]{6}$/i.test(color)) span.style.color = `#${color}`;
    const size = Number(attrByLocal(firstByLocal(rPr, "sz"), "val"));
    if (size) span.style.fontSize = `${Math.max(8, size / 2)}pt`;
  }
  span.textContent = `${tabs ? "\t".repeat(tabs) : ""}${text}${breaks ? "\n".repeat(breaks) : ""}`;
  container.appendChild(span);
}

async function imageUrl(zip, target, registerObjectUrl) {
  if (!target || /^[a-z]+:/i.test(target)) return "";
  const bytes = await zip.read(target);
  if (!bytes) return "";
  const url = URL.createObjectURL(new Blob([bytes], { type: mimeFromFilename(target) }));
  registerObjectUrl?.(url);
  return url;
}

async function renderDocx(arrayBuffer, container, options) {
  const zip = new ZipArchive(arrayBuffer);
  if (!zip.has("word/document.xml")) throw new Error("DOCX_DOCUMENT_XML_MISSING");
  const doc = parseXml(await zip.text("word/document.xml"));
  const rels = await relationshipMap(zip, "word/_rels/document.xml.rels", "word/document.xml");
  const body = firstByLocal(doc, "body");
  const page = makeElement("div", "office-docx-page");
  container.appendChild(page);
  const children = body ? [...body.children] : [];
  for (const node of children) {
    if (node.localName === "p") {
      const style = paragraphStyle(node);
      const p = makeElement("p", `office-docx-p ${style.includes("heading1") ? "h1" : style.includes("heading2") ? "h2" : style.includes("heading3") ? "h3" : style.includes("title") ? "title" : ""}`.trim());
      for (const run of nodesByLocal(node, "r")) appendRun(p, run);
      page.appendChild(p);
      const blips = nodesByLocal(node, "blip");
      for (const blip of blips) {
        const rid = blip.getAttribute("r:embed") || blip.getAttributeNS(NS.relationship, "embed") || attrByLocal(blip, "embed");
        const target = rels.get(rid)?.target || "";
        const url = await imageUrl(zip, target, options.registerObjectUrl);
        if (url) {
          const figure = makeElement("figure", "office-docx-figure");
          const img = document.createElement("img"); img.src = url; img.alt = "문서 이미지"; figure.appendChild(img); page.appendChild(figure);
        }
      }
    } else if (node.localName === "tbl") {
      const wrap = makeElement("div", "office-docx-table-wrap");
      const table = makeElement("table", "office-docx-table");
      for (const trNode of [...node.children].filter(x => x.localName === "tr")) {
        const tr = document.createElement("tr");
        for (const tcNode of [...trNode.children].filter(x => x.localName === "tc")) {
          const td = document.createElement("td");
          const ps = nodesByLocal(tcNode, "p");
          td.textContent = ps.map(pn => nodesByLocal(pn, "t").map(t => t.textContent || "").join("")).join("\n");
          tr.appendChild(td);
        }
        table.appendChild(tr);
      }
      wrap.appendChild(table); page.appendChild(wrap);
    }
  }
  if (!page.textContent?.trim() && !page.querySelector("img")) page.appendChild(makeElement("div", "office-preview-empty", "표시할 DOCX 내용이 없음"));
}

function workbookInfo(arrayBuffer) {
  if (!window.XLSX?.read) return { workbook: null, sheetNames: [], activeIndex: 0 };
  try {
    const workbook = window.XLSX.read(arrayBuffer, { type: "array", cellDates: true, cellStyles: true, dense: false });
    const sheetNames = workbook.SheetNames || [];
    const view = workbook.Workbook?.WBView?.[0] || workbook.Workbook?.Views?.[0] || {};
    const activeIndex = Math.max(0, Math.min(sheetNames.length - 1, Number(view.activeTab ?? 0) || 0));
    return { workbook, sheetNames, activeIndex };
  } catch {
    return { workbook: null, sheetNames: [], activeIndex: 0 };
  }
}

function sanitizeOfficeHtmlDocument(html = "") {
  const doc = new DOMParser().parseFromString(String(html || ""), "text/html");
  doc.querySelectorAll("script,iframe,object,embed,meta,base,form,input,button,textarea,select").forEach(node => node.remove());
  for (const el of [...doc.querySelectorAll("*")]) {
    for (const attr of [...el.attributes]) {
      const name = attr.name.toLowerCase();
      const value = String(attr.value || "").trim();
      if (name.startsWith("on")) el.removeAttribute(attr.name);
      if (["src", "href", "xlink:href"].includes(name) && /^javascript:/i.test(value)) el.removeAttribute(attr.name);
    }
  }
  return {
    styles: [...doc.head.querySelectorAll("style")].map(node => node.textContent || "").join("\n"),
    body: doc.body.innerHTML || html
  };
}

function mountIsolatedHtml(host, html) {
  const { styles, body } = sanitizeOfficeHtmlDocument(html);
  const shadow = host.attachShadow ? host.attachShadow({ mode: "open" }) : null;
  const root = shadow || host;
  const style = document.createElement("style");
  style.textContent = `
    :host{display:block;color:#1e2f40;font-family:"Aptos","Segoe UI","Malgun Gothic",sans-serif}
    *{box-sizing:border-box}
    body{margin:0}
    table{max-width:none}
    ${styles}
  `;
  root.appendChild(style);
  const frame = document.createElement("div");
  frame.className = "xlsx-preview-isolated-frame";
  frame.innerHTML = body;
  root.appendChild(frame);
}

function activateSheetTab(book, index) {
  const tabs = [...book.querySelectorAll("[data-sheet-tab]")];
  const panels = [...book.querySelectorAll("[data-sheet-panel]")];
  tabs.forEach((tab, i) => {
    const active = i === index;
    tab.classList.toggle("active", active);
    tab.setAttribute("aria-selected", active ? "true" : "false");
    tab.tabIndex = active ? 0 : -1;
  });
  panels.forEach((panel, i) => panel.hidden = i !== index);
}

function makeSheetTabs(book, sheetNames, activeIndex = 0) {
  const tabs = makeElement("div", "office-xlsx-tabs");
  tabs.setAttribute("role", "tablist");
  sheetNames.forEach((name, index) => {
    const button = makeElement("button", "office-xlsx-tab", name || `Sheet ${index + 1}`);
    button.type = "button";
    button.dataset.sheetTab = String(index);
    button.setAttribute("role", "tab");
    button.addEventListener("click", () => activateSheetTab(book, index));
    tabs.appendChild(button);
  });
  book.appendChild(tabs);
  requestAnimationFrame(() => activateSheetTab(book, activeIndex));
}

function excelCellValue(cell) {
  if (!cell) return "";
  if (cell.w != null) return String(cell.w);
  if (cell.v instanceof Date) return cell.v.toLocaleString("ko-KR");
  return cell.v == null ? "" : String(cell.v);
}

function excelCellStyle(cell, td) {
  const style = cell?.s;
  if (!style || typeof style !== "object") return;
  const fg = style.fill?.fgColor?.rgb || style.fill?.fgColor?.indexed;
  if (typeof fg === "string" && /^[0-9A-F]{8}$/i.test(fg)) td.style.background = `#${fg.slice(2)}`;
  else if (typeof fg === "string" && /^[0-9A-F]{6}$/i.test(fg)) td.style.background = `#${fg}`;
  const fontColor = style.font?.color?.rgb;
  if (typeof fontColor === "string" && /^[0-9A-F]{8}$/i.test(fontColor)) td.style.color = `#${fontColor.slice(2)}`;
  else if (typeof fontColor === "string" && /^[0-9A-F]{6}$/i.test(fontColor)) td.style.color = `#${fontColor}`;
  if (style.font?.bold) td.style.fontWeight = "700";
  if (style.font?.italic) td.style.fontStyle = "italic";
  if (style.alignment?.horizontal) td.style.textAlign = style.alignment.horizontal === "centerContinuous" ? "center" : style.alignment.horizontal;
  if (style.alignment?.vertical) td.style.verticalAlign = style.alignment.vertical;
  if (style.alignment?.wrapText) td.style.whiteSpace = "pre-wrap";
}

function renderXlsxFallback(arrayBuffer, container, info = workbookInfo(arrayBuffer)) {
  if (!info.workbook || !window.XLSX?.utils) throw new Error("SHEETJS_NOT_LOADED");
  const { workbook, sheetNames, activeIndex } = info;
  const book = makeElement("div", "office-xlsx-book office-xlsx-fallback");
  container.appendChild(book);
  makeSheetTabs(book, sheetNames.slice(0, 24), activeIndex);

  for (let sheetIndex = 0; sheetIndex < Math.min(sheetNames.length, 24); sheetIndex += 1) {
    const name = sheetNames[sheetIndex];
    const sheet = workbook.Sheets[name];
    const panel = makeElement("section", "office-xlsx-panel");
    panel.dataset.sheetPanel = String(sheetIndex);
    panel.setAttribute("role", "tabpanel");
    const tableWrap = makeElement("div", "office-xlsx-table-wrap excel-like-grid");
    const table = document.createElement("table");
    const ref = sheet?.["!ref"] || "A1:A1";
    const range = window.XLSX.utils.decode_range(ref);
    const rowEnd = Math.min(range.e.r, range.s.r + 299);
    const colEnd = Math.min(range.e.c, range.s.c + 79);
    const merges = sheet?.["!merges"] || [];
    const mergeStarts = new Map();
    const covered = new Set();
    for (const merge of merges) {
      if (merge.e.r < range.s.r || merge.e.c < range.s.c || merge.s.r > rowEnd || merge.s.c > colEnd) continue;
      const key = `${merge.s.r}:${merge.s.c}`;
      mergeStarts.set(key, merge);
      for (let r = merge.s.r; r <= Math.min(merge.e.r, rowEnd); r += 1) {
        for (let c = merge.s.c; c <= Math.min(merge.e.c, colEnd); c += 1) if (r !== merge.s.r || c !== merge.s.c) covered.add(`${r}:${c}`);
      }
    }

    const colgroup = document.createElement("colgroup");
    const rowHeaderCol = document.createElement("col");
    rowHeaderCol.style.width = "48px";
    colgroup.appendChild(rowHeaderCol);
    for (let c = range.s.c; c <= colEnd; c += 1) {
      const col = document.createElement("col");
      const meta = sheet?.["!cols"]?.[c] || {};
      const px = Number(meta.wpx) || (Number(meta.wch) ? Number(meta.wch) * 7.4 + 12 : 92);
      col.style.width = `${Math.max(42, Math.min(420, px))}px`;
      colgroup.appendChild(col);
    }
    table.appendChild(colgroup);

    const thead = document.createElement("thead");
    const headerRow = document.createElement("tr");
    headerRow.appendChild(makeElement("th", "excel-corner", ""));
    for (let c = range.s.c; c <= colEnd; c += 1) {
      const th = makeElement("th", "excel-col-header", window.XLSX.utils.encode_col(c));
      headerRow.appendChild(th);
    }
    thead.appendChild(headerRow); table.appendChild(thead);
    const tbody = document.createElement("tbody");
    for (let r = range.s.r; r <= rowEnd; r += 1) {
      const tr = document.createElement("tr");
      const rowMeta = sheet?.["!rows"]?.[r] || {};
      const rowPx = Number(rowMeta.hpx) || (Number(rowMeta.hpt) ? Number(rowMeta.hpt) * 96 / 72 : 24);
      tr.style.height = `${Math.max(20, Math.min(220, rowPx))}px`;
      tr.appendChild(makeElement("th", "excel-row-header", String(r + 1)));
      for (let c = range.s.c; c <= colEnd; c += 1) {
        const key = `${r}:${c}`;
        if (covered.has(key)) continue;
        const td = document.createElement("td");
        const merge = mergeStarts.get(key);
        if (merge) {
          td.rowSpan = Math.min(merge.e.r, rowEnd) - merge.s.r + 1;
          td.colSpan = Math.min(merge.e.c, colEnd) - merge.s.c + 1;
        }
        const address = window.XLSX.utils.encode_cell({ r, c });
        const cell = sheet?.[address];
        td.textContent = excelCellValue(cell);
        td.title = td.textContent;
        excelCellStyle(cell, td);
        tr.appendChild(td);
      }
      tbody.appendChild(tr);
    }
    table.appendChild(tbody); tableWrap.appendChild(table); panel.appendChild(tableWrap); book.appendChild(panel);
    if (range.e.r > rowEnd || range.e.c > colEnd) panel.appendChild(makeElement("small", "office-preview-limit", `빠른 Preview를 위해 ${rowEnd - range.s.r + 1}행 × ${colEnd - range.s.c + 1}열까지 표시함.`));
  }
  activateSheetTab(book, activeIndex);
}

async function renderXlsx(arrayBuffer, container) {
  const info = workbookInfo(arrayBuffer);
  const sheetNames = info.sheetNames.slice(0, 24);
  try {
    const module = await import("xlsx-preview");
    const renderer = module.default || module;
    if (typeof renderer?.xlsx2Html !== "function") throw new Error("XLSX_PREVIEW_ENGINE_INVALID");
    const result = await renderer.xlsx2Html(arrayBuffer.slice(0), {
      output: "string",
      separateSheets: true,
      minimumRows: 20,
      minimumCols: 12
    });
    const pages = Array.isArray(result) ? result : [result];
    if (!pages.length) throw new Error("XLSX_PREVIEW_EMPTY");
    const book = makeElement("div", "office-xlsx-book office-xlsx-high-fidelity");
    container.appendChild(book);
    const labels = sheetNames.length ? sheetNames : pages.map((_, i) => `Sheet ${i + 1}`);
    makeSheetTabs(book, labels.slice(0, pages.length), Math.min(info.activeIndex, pages.length - 1));
    pages.forEach((html, index) => {
      const panel = makeElement("section", "office-xlsx-panel high-fidelity-sheet");
      panel.dataset.sheetPanel = String(index);
      panel.setAttribute("role", "tabpanel");
      const viewport = makeElement("div", "office-xlsx-rich-viewport");
      mountIsolatedHtml(viewport, html);
      panel.appendChild(viewport); book.appendChild(panel);
    });
    activateSheetTab(book, Math.min(info.activeIndex, pages.length - 1));
  } catch (error) {
    console.warn("[NAYSOR] styled XLSX preview fallback:", error?.message || error);
    renderXlsxFallback(arrayBuffer, container, info);
  }
}

function officeColor(node, fallback = "#ffffff") {
  const solid = firstByLocal(node, "solidFill");
  const srgb = solid ? firstByLocal(solid, "srgbClr") : null;
  const value = attrByLocal(srgb, "val");
  return /^[0-9a-f]{6}$/i.test(value) ? `#${value}` : fallback;
}

function xfrmBox(node, slideWidth, slideHeight) {
  const xfrm = firstByLocal(node, "xfrm");
  if (!xfrm) return null;
  const off = firstByLocal(xfrm, "off");
  const ext = firstByLocal(xfrm, "ext");
  const x = Number(attrByLocal(off, "x") || 0), y = Number(attrByLocal(off, "y") || 0);
  const cx = Number(attrByLocal(ext, "cx") || 0), cy = Number(attrByLocal(ext, "cy") || 0);
  if (!slideWidth || !slideHeight || (!cx && !cy)) return null;
  return { left: `${x / slideWidth * 100}%`, top: `${y / slideHeight * 100}%`, width: `${cx / slideWidth * 100}%`, height: `${cy / slideHeight * 100}%` };
}

function applyBox(el, box) {
  if (!box) return;
  Object.assign(el.style, { position: "absolute", left: box.left, top: box.top, width: box.width, height: box.height });
}

async function pptxSlideSize(arrayBuffer) {
  try {
    const zip = new ZipArchive(arrayBuffer);
    if (!zip.has("ppt/presentation.xml")) return { width: 12192000, height: 6858000 };
    const pres = parseXml(await zip.text("ppt/presentation.xml"));
    const sldSz = firstByLocal(pres, "sldSz");
    return {
      width: Number(attrByLocal(sldSz, "cx")) || 12192000,
      height: Number(attrByLocal(sldSz, "cy")) || 6858000
    };
  } catch {
    return { width: 12192000, height: 6858000 };
  }
}

async function renderPptx(arrayBuffer, container, options) {
  try {
    const module = await import("pptx-preview");
    const init = module.init || module.default?.init;
    if (typeof init !== "function") throw new Error("PPTX_PREVIEW_ENGINE_INVALID");
    const size = await pptxSlideSize(arrayBuffer);
    const available = Math.max(720, Math.min(1240, Number(container.clientWidth || container.parentElement?.clientWidth || 1040) - 8));
    const height = Math.max(405, Math.round(available * size.height / size.width));
    const deck = makeElement("div", "office-pptx-library-deck");
    const mount = makeElement("div", "office-pptx-library-mount");
    mount.style.setProperty("--pptx-native-width", `${available}px`);
    deck.appendChild(mount); container.appendChild(deck);
    const previewer = init(mount, { width: available, height });
    await previewer.preview(arrayBuffer.slice(0));
    if (!mount.children.length) throw new Error("PPTX_PREVIEW_ENGINE_EMPTY");
    mount.querySelectorAll("canvas,svg,img").forEach(node => node.setAttribute("draggable", "false"));
  } catch (error) {
    console.warn("[NAYSOR] high-fidelity PPTX preview fallback:", error?.message || error);
    await renderPptxFallback(arrayBuffer, container, options);
  }
}

async function renderPptxFallback(arrayBuffer, container, options) {
  const zip = new ZipArchive(arrayBuffer);
  let slideWidth = 12192000, slideHeight = 6858000;
  if (zip.has("ppt/presentation.xml")) {
    const pres = parseXml(await zip.text("ppt/presentation.xml"));
    const sldSz = firstByLocal(pres, "sldSz");
    slideWidth = Number(attrByLocal(sldSz, "cx")) || slideWidth;
    slideHeight = Number(attrByLocal(sldSz, "cy")) || slideHeight;
  }
  const slides = zip.names().filter(name => /^ppt\/slides\/slide\d+\.xml$/i.test(name)).sort((a, b) => Number(a.match(/slide(\d+)/i)?.[1] || 0) - Number(b.match(/slide(\d+)/i)?.[1] || 0));
  const deck = makeElement("div", "office-pptx-deck"); container.appendChild(deck);
  for (let index = 0; index < slides.length; index += 1) {
    const slidePath = slides[index];
    const xml = parseXml(await zip.text(slidePath));
    const relPath = slidePath.replace("ppt/slides/", "ppt/slides/_rels/") + ".rels";
    const rels = await relationshipMap(zip, relPath, slidePath);
    const card = makeElement("section", "office-pptx-card");
    card.appendChild(makeElement("span", "office-pptx-number", String(index + 1)));
    const slide = makeElement("div", "office-pptx-slide");
    slide.style.aspectRatio = `${slideWidth} / ${slideHeight}`;
    const cSld = firstByLocal(xml, "cSld");
    if (cSld) slide.style.background = officeColor(cSld, "#fff");

    for (const shape of nodesByLocal(xml, "sp")) {
      const text = nodesByLocal(shape, "t").map(t => t.textContent || "").join("\n").trim();
      if (!text) continue;
      const box = xfrmBox(shape, slideWidth, slideHeight);
      const item = makeElement("div", "office-pptx-text", text); applyBox(item, box);
      const rPr = firstByLocal(shape, "rPr") || firstByLocal(shape, "defRPr");
      const size = Number(attrByLocal(rPr, "sz"));
      if (size) item.style.fontSize = `${Math.max(7, size / 100)}pt`;
      if (attrByLocal(rPr, "b") === "1") item.style.fontWeight = "700";
      const color = attrByLocal(firstByLocal(rPr || shape, "srgbClr"), "val");
      if (/^[0-9a-f]{6}$/i.test(color)) item.style.color = `#${color}`;
      slide.appendChild(item);
    }

    for (const pic of nodesByLocal(xml, "pic")) {
      const box = xfrmBox(pic, slideWidth, slideHeight);
      const blip = firstByLocal(pic, "blip");
      const rid = blip?.getAttribute("r:embed") || blip?.getAttributeNS(NS.relationship, "embed") || attrByLocal(blip, "embed");
      const target = rels.get(rid)?.target || "";
      const url = await imageUrl(zip, target, options.registerObjectUrl);
      if (!url) continue;
      const img = document.createElement("img"); img.className = "office-pptx-image"; img.src = url; img.alt = "슬라이드 이미지"; applyBox(img, box); slide.appendChild(img);
    }

    for (const frame of nodesByLocal(xml, "graphicFrame")) {
      const tableNode = firstByLocal(frame, "tbl");
      if (!tableNode) continue;
      const box = xfrmBox(frame, slideWidth, slideHeight);
      const wrap = makeElement("div", "office-pptx-table"); applyBox(wrap, box);
      const table = document.createElement("table");
      for (const trNode of nodesByLocal(tableNode, "tr")) {
        const tr = document.createElement("tr");
        for (const tc of [...trNode.children].filter(n => n.localName === "tc")) {
          const td = document.createElement("td"); td.textContent = nodesByLocal(tc, "t").map(t => t.textContent || "").join(" "); tr.appendChild(td);
        }
        table.appendChild(tr);
      }
      wrap.appendChild(table); slide.appendChild(wrap);
    }

    card.appendChild(slide); deck.appendChild(card);
  }
  if (!slides.length) deck.appendChild(makeElement("div", "office-preview-empty", "PPTX 슬라이드를 찾지 못함"));
}

function rtfToText(text) {
  return text
    .replace(/\\par[d]?\b/g, "\n")
    .replace(/\\tab\b/g, "\t")
    .replace(/\\'[0-9a-fA-F]{2}/g, match => String.fromCharCode(parseInt(match.slice(2), 16)))
    .replace(/\\[a-zA-Z]+-?\d* ?/g, "")
    .replace(/[{}]/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function renderText(arrayBuffer, container, mimeType) {
  let text = new TextDecoder("utf-8").decode(arrayBuffer);
  if (mimeType.includes("rtf")) text = rtfToText(text);
  const pre = makeElement("pre", "office-text-preview", text); container.appendChild(pre);
}

export function isOfficeSelfPreviewMime(mimeType = "") {
  const mime = String(mimeType).toLowerCase();
  return [MIME.docx, MIME.xlsx, MIME.pptx, MIME.csv, MIME.text, MIME.rtf, "text/rtf"].includes(mime);
}

export async function renderOfficePreview(arrayBuffer, mimeType, container, options = {}) {
  if (!container) return;
  container.replaceChildren();
  const stage = makeElement("div", "office-preview-stage");
  stage.style.setProperty("--preview-zoom", String(options.zoom || 1));
  container.appendChild(stage);
  const mime = String(mimeType || "").toLowerCase();
  if (mime === MIME.docx) await renderDocx(arrayBuffer, stage, options);
  else if (mime === MIME.xlsx) await renderXlsx(arrayBuffer, stage);
  else if (mime === MIME.pptx) await renderPptx(arrayBuffer, stage, options);
  else if ([MIME.csv, MIME.text, MIME.rtf, "text/rtf"].includes(mime)) renderText(arrayBuffer, stage, mime);
  else throw new Error(`OFFICE_SELF_PREVIEW_UNSUPPORTED_${mime || "UNKNOWN"}`);
}
